import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { GET_ALL_INVESTMENTS } from '@/apollo/schemas/investments/getAllInvestments';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/CoginitoAuthProvider';

import AdminListTable from '../admins/AdminListTable';
import {
  InvestmentsTableColumns,
  TInvestment,
} from './InvestmentsTableColumns';

type PaymentTypeTab = 'crypto' | 'fiat';

const InvestmentsTable = () => {
  const { session } = useAuth();

  const [paymentType, setPaymentType] = useState<PaymentTypeTab>('crypto');

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const authToken = session
    ? session?.getIdToken().getJwtToken()
    : localStorage.getItem('idToken');

  const { data, refetch, loading } = useQuery(GET_ALL_INVESTMENTS, {
    variables: {
      input: {
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        isCrypto: paymentType === 'crypto',
      },
    },
    context: {
      headers: {
        identity: authToken,
      },
    },
  });

  useEffect(() => {
    refetch({
      input: {
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        isCrypto: paymentType === 'crypto',
      },
    });
  }, [pagination, paymentType, refetch]);

  const handlePaymentTypeChange = (value: string) => {
    setPaymentType(value as PaymentTypeTab);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const list = data?.getAllInvestments;

  const rows: TInvestment[] = list?.investments ?? [];
  const total = list?.total ?? 0;

  return (
    <div className="h-full p-4 border border-secondary rounded-lg bg-white shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Investments</h2>
          <p className="text-sm text-muted-foreground">
            Monitor all customer investments and their current status. Open an
            investment for its details, documents, accreditation and payment
            actions.
          </p>
        </div>
      </div>
      <Tabs
        value={paymentType}
        onValueChange={handlePaymentTypeChange}
        className="mb-4"
      >
        <TabsList>
          <TabsTrigger value="crypto">Crypto</TabsTrigger>
          <TabsTrigger value="fiat">Fiat</TabsTrigger>
        </TabsList>
      </Tabs>
      <AdminListTable
        refetchData={refetch}
        pagination={pagination}
        setPagination={setPagination}
        columns={InvestmentsTableColumns}
        data={rows}
        rowCount={total}
        isLoadingData={loading}
      />
    </div>
  );
};

export default InvestmentsTable;
